import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, CalendarRange, FileText } from 'lucide-react';
import MetricsGrid from '@components/common/MetricsGrid';
import MetricCard from '@components/common/MetricCard';
import { buildAssessorDashboardRoutes } from '@utils/assessorDashboardRoutes';
import type { AssessorMetricsData } from '@app-types';
import styles from './AssessorMetrics.module.css';

interface AssessorMetricsProps {
  data: AssessorMetricsData | undefined;
  isLoading: boolean;
  userId: number;
}

const AssessorMetrics: React.FC<AssessorMetricsProps> = ({ data, isLoading, userId }) => {
  const navigate = useNavigate();
  // Without data there is no server window to build dates from, so the cards stay inert until it arrives.
  const routes = data ? buildAssessorDashboardRoutes(data, userId) : undefined;
  const go = (path: string | undefined) => (path ? () => navigate(path) : undefined);

  return (
    <MetricsGrid>
      <MetricCard
        icon={<Calendar size={24} />}
        label="Meetings Today"
        value={data?.meetings_today ?? 0}
        subtitle="Open meetings scheduled today"
        variant="blue"
        loading={isLoading}
        onClick={go(routes?.meetingsToday)}
      />
      <MetricCard
        icon={<CalendarRange size={24} />}
        label="Meetings Next 5 Days"
        value={data?.meetings_next_5_days ?? 0}
        subtitle="Next 5 business days"
        variant="purple"
        loading={isLoading}
        onClick={go(routes?.meetingsNext5Days)}
      >
        <div className={styles.badges}>
          <span className={styles.badgeDanger}>
            In Conflict <strong>{data?.meetings_next_5_days_in_conflict ?? 0}</strong>
          </span>
          <span className={styles.badgeNeutral}>
            Informational <strong>{data?.meetings_next_5_days_informational ?? 0}</strong>
          </span>
        </div>
      </MetricCard>
      <MetricCard
        icon={<FileText size={24} />}
        label="Assessments Open"
        value={data?.assessments_open ?? 0}
        subtitle="Pending, evidence due, past due or escalated"
        variant="orange"
        loading={isLoading}
        onClick={go(routes?.assessmentsOpen)}
      />
    </MetricsGrid>
  );
};

export { AssessorMetrics };
